import React from 'react';
import { Box, Text } from '@chakra-ui/react';
import useColors from '../../hooks/useColors';

const API_URL = process.env.REACT_APP_API_URL || '';

// Role-aware assignee chips:
//   owner/admin → can toggle anyone
//   member      → can only toggle themselves
//   viewer      → read-only (backend blocks writes anyway)
// members: workspace.members ([{ user: {_id,name,avatar}, role }])
// value: array of selected user id strings
const AssigneePicker = ({ members = [], value = [], onChange, currentUserId, currentUserRole }) => {
  const { border, hoverBg, textPrimary } = useColors();
  const canAssignOthers = currentUserRole === 'owner' || currentUserRole === 'admin';

  const toggle = (id) => {
    if (!(canAssignOthers || id === String(currentUserId))) return;
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  // Only members whose `user` was populated (has a name) can be shown as chips.
  // If the API returned bare ids (e.g. an old backend without the deep populate),
  // say so explicitly instead of rendering an empty box.
  const usable = members.filter((m) => m.user && typeof m.user === 'object' && m.user.name);

  if (!members.length) {
    return (
      <Text fontSize="sm" color="gray.500">
        No members to assign
      </Text>
    );
  }

  if (!usable.length) {
    return (
      <Text fontSize="sm" color="gray.500">
        Couldn’t load members — try refreshing the page.
      </Text>
    );
  }

  return (
    <Box display="flex" gap={2} flexWrap="wrap">
      {usable.map((m) => {
        const u = m.user;
        const id = String(u._id || '');
        if (!id) return null;
        const selected = value.includes(id);
        const isSelf = id === String(currentUserId);
        const allowed = canAssignOthers || isSelf;
        const initials =
          u.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
        const hasRealAvatar = u.avatar && !u.avatar.includes('ui-avatars.com');
        const avatarSrc = hasRealAvatar
          ? u.avatar.startsWith('/uploads/')
            ? `${API_URL}${u.avatar}`
            : u.avatar
          : null;

        return (
          <Box
            key={id}
            onClick={() => toggle(id)}
            title={allowed ? '' : 'Only the workspace owner or admins can assign other members'}
            display="flex"
            alignItems="center"
            gap={2}
            pl={1}
            pr={3}
            py={1}
            borderRadius="full"
            border="1px solid"
            borderColor={selected ? '#7a1f3d' : border}
            bg={selected ? 'rgba(122,31,61,0.12)' : hoverBg}
            opacity={allowed ? 1 : 0.45}
            cursor={allowed ? 'pointer' : 'not-allowed'}
            transition="all 0.15s"
            _hover={allowed ? { borderColor: '#cd6785' } : {}}
          >
            <Box
              w="22px"
              h="22px"
              borderRadius="full"
              overflow="hidden"
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="9px"
              fontWeight="bold"
              color="white"
              style={avatarSrc ? {} : { background: 'linear-gradient(to right, #7a1f3d, #a83a58)' }}
            >
              {avatarSrc ? (
                <Box as="img" src={avatarSrc} alt={u.name} w="100%" h="100%" style={{ objectFit: 'cover' }} />
              ) : (
                initials
              )}
            </Box>
            <Text fontSize="sm" color={textPrimary}>
              {u.name}
              {isSelf ? ' (you)' : ''}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
};

export default AssigneePicker;
